import React, { useState, useMemo } from 'react';
import { Card, Button, Input } from '../ui';
import { TrendingUp, Plus, Trash2, CheckSquare, BarChart3, AlertCircle, RefreshCw } from 'lucide-react';

interface BudgetItem {
  id: string;
  category: string;
  budgeted: number;
  actual: number;
}

const DEFAULT_ITEMS: BudgetItem[] = [
  { id: '1', category: 'Field Trip Transportation', budgeted: 3000, actual: 2850 },
  { id: '2', category: 'Classroom Support Supplies', budgeted: 5000, actual: 5400 }, // Over budget
  { id: '3', category: 'Teacher Appreciation Luncheon', budgeted: 1500, actual: 1200 },
  { id: '4', category: 'School Playground Repairs', budgeted: 10000, actual: 10800 }, // Over budget
  { id: '5', category: 'Booster Club Admin & Web Fees', budgeted: 800, actual: 800 }
];

export function PtaBudgetVsActuals() {
  const [items, setItems] = useState<BudgetItem[]>(DEFAULT_ITEMS);

  // Form states
  const [newCategory, setNewCategory] = useState('');
  const [newBudgeted, setNewBudgeted] = useState('1000');
  const [newActual, setNewActual] = useState('0');

  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    setItems(prev => [
      ...prev,
      {
        id: `bi_${Date.now()}`,
        category: newCategory.trim(),
        budgeted: parseFloat(newBudgeted) || 0,
        actual: parseFloat(newActual) || 0
      }
    ]);

    setNewCategory('');
    setNewActual('0');
  };

  const loadSample = () => {
    setItems(DEFAULT_ITEMS);
  };

  const clearAll = () => {
    setItems([]);
  };

  // Calculations
  const totals = useMemo(() => {
    return items.reduce((acc, item) => {
      acc.budgeted += item.budgeted;
      acc.actual += item.actual;
      return acc;
    }, { budgeted: 0, actual: 0 });
  }, [items]);

  const totalVariance = totals.budgeted - totals.actual;
  const totalVariancePercent = totals.budgeted > 0 ? (totalVariance / totals.budgeted) * 100 : 0;

  const itemDetails = useMemo(() => {
    return items.map(item => {
      const variance = item.budgeted - item.actual;
      const variancePercent = item.budgeted > 0 ? (variance / item.budgeted) * 100 : 0;
      const percentSpent = item.budgeted > 0 ? Math.min(100, (item.actual / item.budgeted) * 100) : 0;
      const isOver = item.actual > item.budgeted;

      return {
        ...item,
        variance,
        variancePercent,
        percentSpent,
        isOver
      };
    });
  }, [items]);

  return (
    <div className="space-y-8">
      {/* Intro Banner */}
      <div className="bg-[#0044FF] border-4 border-black text-white p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white">
        <h3 className="font-display font-black text-2xl uppercase tracking-tight mb-2">PTA / PTO Budget vs. Actuals Modeler</h3>
        <p className="font-bold text-sm max-w-xl text-blue-100 dark:text-gray-300">
          Track spending performance across school events and boosters. Calculate budget variances and verify compliance records.
        </p>
        <div className="flex gap-3 mt-4">
          <Button onClick={loadSample} variant="secondary" className="bg-[#FFD600] text-black hover:bg-white border-2 border-black rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            <RefreshCw size={12} className="mr-2" /> Load Demo
          </Button>
          <Button onClick={clearAll} variant="ghost" className="border-2 border-white text-white hover:bg-red-600 hover:text-white rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            Reset
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        
        {/* Form Column */}
        <div className="lg:col-span-1 bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
          <h4 className="font-display font-black text-xl uppercase tracking-tight flex items-center gap-2">
            <BarChart3 className="text-[#0044FF]" size={20} /> Add Line Item
          </h4>
          
          <form onSubmit={handleAddItem} className="space-y-4 text-sm">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Category Title</label>
              <Input 
                value={newCategory} 
                onChange={e => setNewCategory(e.target.value)} 
                placeholder="e.g. Teacher Support Lunch" 
                className="border-2 border-black rounded-none h-10 font-bold" 
              />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Budgeted ($)</label>
                <Input 
                  type="number" 
                  value={newBudgeted} 
                  onChange={e => setNewBudgeted(e.target.value)} 
                  className="border-2 border-black rounded-none h-10 font-mono font-bold" 
                />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Actual Spent ($)</label>
                <Input 
                  type="number" 
                  value={newActual} 
                  onChange={e => setNewActual(e.target.value)} 
                  className="border-2 border-black rounded-none h-10 font-mono font-bold" 
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#FFD600] text-black border-2 border-black rounded-none font-bold uppercase tracking-wider py-2 hover:bg-black hover:text-white"
            >
              Add Item
            </Button>
          </form>
        </div>

        {/* Dashboard Matrix */}
        <div className="lg:col-span-2 space-y-6 text-sm">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4 font-mono font-bold">
            <div className="bg-[#F5F5F0] border-4 border-black p-4 text-center dark:bg-slate-900">
              <span className="text-[10px] uppercase block text-gray-500 mb-1">Total Budgeted</span>
              <span className="text-2xl font-black font-display text-black dark:text-white">${totals.budgeted.toLocaleString()}</span>
            </div>
            <div className="bg-[#F5F5F0] border-4 border-black p-4 text-center dark:bg-slate-900">
              <span className="text-[10px] uppercase block text-gray-500 mb-1">Total Spent</span>
              <span className="text-2xl font-black font-display text-black dark:text-white">${totals.actual.toLocaleString()}</span>
            </div>
            <div className="bg-[#F5F5F0] border-4 border-black p-4 text-center dark:bg-slate-900 border-l-4 border-l-[#0044FF]">
              <span className="text-[10px] uppercase block text-gray-500 mb-1">Variance Balance</span>
              <span className={`text-2xl font-black font-display ${totalVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {totalVariance >= 0 ? '+' : '-'}${Math.abs(totalVariance).toLocaleString()}
              </span>
              <span className="text-[9px] block text-gray-400 mt-1 uppercase">
                ({totalVariance >= 0 ? 'UNDER' : 'OVER'} BUDGET)
              </span>
            </div>
          </div>

          {/* Table display */}
          <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
            <h4 className="font-display font-black text-xl uppercase tracking-tight">Variance Sheet Ledger</h4>

            {items.length === 0 ? (
              <p className="text-sm font-mono text-gray-400 py-8 border-2 border-dashed border-gray-300 text-center uppercase">Add budget categories to populate dashboard.</p>
            ) : (
              <div className="space-y-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-[#F5F5F0] border-b-4 border-black font-mono font-bold uppercase tracking-wider text-black dark:bg-slate-900 dark:border-white dark:text-white">
                        <th className="py-2 px-3">Category</th>
                        <th className="py-2 px-3 text-right">Budget ($)</th>
                        <th className="py-2 px-3 text-right">Actual Spent ($)</th>
                        <th className="py-2 px-3 text-right">Variance ($)</th>
                        <th className="py-2 px-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y-2 divide-gray-200 dark:divide-slate-800 font-bold">
                      {itemDetails.map(item => (
                        <tr key={item.id} className="hover:bg-[#FFD600]/10 transition-colors">
                          <td className="py-2 px-3 font-display uppercase tracking-tight text-sm text-black dark:text-white">
                            {item.category}
                          </td>
                          <td className="py-2 px-3 font-mono text-right">${item.budgeted.toLocaleString()}</td>
                          <td className="py-2 px-3 font-mono text-right">${item.actual.toLocaleString()}</td>
                          <td className={`py-2 px-3 font-mono text-right text-base ${item.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {item.variance >= 0 ? '+' : '-'}${Math.abs(item.variance).toLocaleString()}
                            <span className="text-[8px] font-mono block text-gray-400">({Math.abs(item.variancePercent).toFixed(1)}%)</span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            <button onClick={() => setItems(prev => prev.filter(i => i.id !== item.id))} className="text-red-500 hover:text-red-700">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Progress bars visual explanation */}
                <div className="pt-4 border-t-2 border-black space-y-4">
                  <h5 className="font-mono text-xs font-bold uppercase tracking-wider text-gray-500">Spending Usage Ratios</h5>
                  {itemDetails.map(item => (
                    <div key={item.id} className="space-y-1.5">
                      <div className="flex justify-between text-xs font-bold">
                        <span className="uppercase text-slate-700 dark:text-slate-300">{item.category}</span>
                        <span className="font-mono text-[10px]">{Math.round((item.actual / Math.max(1, item.budgeted)) * 100)}% Spent</span>
                      </div>
                      <div className="h-4 border-2 border-black bg-white dark:bg-black w-full relative">
                        <div 
                          className={`absolute top-0 left-0 h-full transition-all duration-500 ${
                            item.isOver ? 'bg-red-500' : 'bg-[#0044FF]'
                          }`}
                          style={{ width: `${Math.min(100, (item.actual / Math.max(1, item.budgeted)) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            )}
          </div>
          
        </div>

      </div>

    </div>
  );
}
